import Badge from './Badge';
import { formatAmount, useBoxWidth } from './Chart';
import { meterState } from './meterState';

export { meterState };

// §2.6 (rev 2.3) Meter: a goal meter, not a stat tile — the figure, its target and its pace. The figure leads in t-figure;
// under it a bullet bar: the fill is the amount (the accent; at half strength while behind pace or under target), a pace
// tick (ink-3: where the amount should be by now, today's ranges only) and the target tick at the bar's end.
//   value, target (null: no target set), pace (0..1 of the target due by now; null outside today), unit 'words' | 'min'.
//   size 'default' (heading, figure, badge, bar, legend) | 'small' (one row: name, bar, figure / target).
//   period: the words after the target on the default size ("today", "this week"). aside: the sentence shown instead of
//   the bar when there is no target (default: where targets are set). sub: a quiet word after the label ("writers");
//   mark: the legend's word for the fill (default "approved").
export function meterSentence(value, target, pace, unit) {
  const u = unit === 'min' ? 'minutes' : 'words';
  if (target == null) return `${formatAmount(value, unit)} ${u}, no target set.`;
  const due = pace != null ? ` Pace: ${formatAmount(pace * target, unit)} due by now.` : '';
  return `${formatAmount(value, unit)} of ${formatAmount(target, unit)} ${u}.${due}`;
}

function Bullet({ value, target, pace, unit, small }) {
  const [ref, w] = useBoxWidth(small ? 200 : 480);
  const h = small ? 8 : 12, tick = small ? 3 : 4, H = h + 2 * tick;
  const W = Math.max(w, 40);
  const st = meterState(value, target, pace);
  const fill = Math.max(0, Math.min(1, value / target)) * W;
  const at = f => Math.min(W - 2, Math.max(0, f * W - 1));
  const sentence = meterSentence(value, target, pace, unit);
  return (
    <div className="ui-goal__bar" ref={ref}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={sentence}>
        <title>{sentence}</title>
        <rect className="ui-goal__track" x="0" y={tick} width={W} height={h} rx="2" />
        {fill > 0 && <rect className={`ui-mark${st && st.under ? ' ui-mark--under' : ''}`} x="0" y={tick} width={fill} height={h} rx="2" />}
        {pace != null && <rect className="ui-goal__pace" x={at(pace)} y={tick - (small ? 2 : 3)} width="2" height={h + 2 * (small ? 2 : 3)} />}
        <rect className="ui-goal__target" x={W - 2} y="0" width="2" height={H} />
      </svg>
    </div>
  );
}

export default function Meter({ label, sub, name, value, target, pace = null, unit = 'words', size = 'default', period, aside, details, mark = 'approved' }) {
  const st = meterState(value, target, pace);
  if (size === 'small') {
    return (
      <div className="ui ui-goal ui-goal--small">
        <span className="ui-goal__name">{name}</span>
        {target != null && target > 0
          ? <Bullet value={value} target={target} pace={pace} unit={unit} small />
          : <span className="ui-goal__none">{aside ?? 'No target set'}</span>}
        <span className="ui-goal__sm-fig ui-num">
          <b>{formatAmount(value, unit)}</b>{target != null && target > 0 && <> / {formatAmount(target, unit)}{unit === 'min' ? ' min' : ''}</>}
        </span>
      </div>
    );
  }
  return (
    <div className="ui ui-goal">
      <div className="ui-goal__head">
        <h3 className="ui-goal__label">{label}</h3>
        {sub && <span className="ui-goal__sub">{sub}</span>}
        {st && <Badge tone={st.tone}>{st.label}</Badge>}
        <span className="ui-goal__fig ui-num">
          {formatAmount(value, unit)}
          {target != null && target > 0
            ? <small>/ {formatAmount(target, unit)}{unit === 'min' ? ' min' : ''}{period ? ` ${period}` : ''}</small>
            : <small>{unit === 'min' ? 'min' : 'words'}{period ? ` ${period}` : ''}</small>}
        </span>
      </div>
      {target != null && target > 0 ? (
        <>
          <Bullet value={value} target={target} pace={pace} unit={unit} />
          <div className="ui-goal__legend" aria-hidden="true">
            <span><i className="ui-goal__key" />{mark}</span>
            {pace != null && <span><i className="ui-goal__key ui-goal__key--pace" />pace, now</span>}
            <span><i className="ui-goal__key ui-goal__key--target" />target</span>
          </div>
        </>
      ) : (
        <p className="ui-goal__none">{aside || 'No daily target is set for this range. Admins and Managers set targets in Admin, Targets.'}</p>
      )}
      {details}
    </div>
  );
}
